import React, { useEffect, useMemo, useState } from 'react';
import { FileArchive, FileText, History, LogOut, PauseCircle, PlayCircle, RotateCcw, Trash2, UserCheck } from 'lucide-react';
import { Banner, ConfirmDialog, ReasonDialog, useToast } from '../../common';
import Dialog from '../../common/Dialog';
import { useCanPerform } from '../../../hooks/useCanPerform';
import {
  fetchPatientById,
  fetchPatientHoldHistory,
  readmitPatient,
  resumePatient,
} from '../../../services/admin/patient.service';
import { PatientLifecycleService, isApprovedPa } from '../../../services/patientmr/patientLifecycle.service';
import { SubscriptionService } from '../../../services/billing/subscription.service';
import { describeApiError, getApiErrorCode } from '../../../utils/apiErrors';
import {
  HOLD_PA_REVIEW_DAYS,
  dischargeReasonLabel,
  holdReasonLabel,
} from '../../../constants/patientLifecycle';
import { notifyPatientChartUpdated } from '../../../constants/navigationEvents';
import { getPatientAgeBanners, parseDateParts } from '../../../utils/patientAgeBanners';
import type { AdmitPatientResponse, PatientData, PatientHoldHistoryEntry } from '../../../dtos/admin/patient.dto';
import type { PatientDashboardData, SOCChecklist } from '../../../dtos/patientmr/certificationPeriod.dto';
import HoldDialog from './HoldDialog';
import DischargeDialog from './DischargeDialog';
import AdmitDialog from './AdmitDialog';
import ExportDialog from '../../admin/export/ExportDialog';
import DischargeSummaryBanner from '../discharge/DischargeSummaryBanner';
import { DischargeSummaryService } from '../../../services/patientmr/dischargeSummary.service';
import type { ExportKind } from '../../../dtos/admin/export.dto';

/** MM/DD/YYYY from 'YYYY-MM-DD…' without timezone shifts; '—' when missing. */
export function formatUsDate(value: string | null | undefined): string {
  const parts = parseDateParts(value ?? null);
  if (!parts) return '—';
  return `${String(parts.month).padStart(2, '0')}/${String(parts.day).padStart(2, '0')}/${parts.year}`;
}

/** Whole days since a YYYY-MM-DD date (local calendar). Null when unparseable. */
export function daysSince(value: string | null | undefined, today: Date = new Date()): number | null {
  const parts = parseDateParts(value ?? null);
  if (!parts) return null;
  const start = new Date(parts.year, parts.month - 1, parts.day).getTime();
  const now = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.round((now - start) / (24 * 60 * 60 * 1000));
}

const CORE_SOC_FLAGS: (keyof SOCChecklist)[] = [
  'referralReceived',
  'demographicsComplete',
  'insuranceVerified',
  'careTeamAssigned',
  'socAssessmentComplete',
  'carePlansComplete',
  'physicianOrderComplete',
  'priorAuthComplete',
  'consentObtained',
  'dmeConfirmed',
  'emergencyPlanOnFile',
  // Phase 7: only present (and only ever false) when EVV is required
  'hhaxSetupComplete',
];

/**
 * SOC-07: true when the dashboard says a live track's onboarding checklist is incomplete.
 * Uses the server's `isComplete` when present; otherwise falls back to the flags for tracks
 * still in SOC_IN_PROGRESS (older backends).
 */
export function isSocChecklistIncomplete(dashboard: PatientDashboardData | null): boolean {
  const tracks = dashboard?.serviceTracks ?? [];
  return tracks.some((track) => {
    if (track.phase === 'DISCHARGED' || track.phase === 'NOT_ORDERED') return false;
    if (typeof track.isComplete === 'boolean') return !track.isComplete;
    if (track.phase !== 'SOC_IN_PROGRESS' || !track.socChecklist) return false;
    return CORE_SOC_FLAGS.some((flag) => track.socChecklist[flag] === false);
  });
}

export interface PatientLifecycleBarProps {
  patient: PatientData | null;
  dashboard: PatientDashboardData | null;
  /** Called with the refreshed patient after any status change */
  onPatientChanged: (patient: PatientData) => void;
  /** Switch the chart to a tab (e.g. 'Assessments', 'Prior Authorizations', 'Overview') */
  onNavigateTab: (tab: string) => void;
  /** Phase 13: the sample patient was deleted; leave the chart for the patient list. */
  onSampleDeleted?: () => void;
}

const actionButtonClass =
  'inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-sm font-medium disabled:opacity-50 min-h-[36px]';

/**
 * Lifecycle banners and actions under the patient header (Phase 6): pending admission + Admit,
 * hold banner + Resume + PA review, discharged banner + Readmit, age banners, SOC nudge,
 * and the Hold / Discharge actions.
 */
const PatientLifecycleBar: React.FC<PatientLifecycleBarProps> = ({ patient, dashboard, onPatientChanged, onNavigateTab, onSampleDeleted }) => {
  const { showToast } = useToast();
  const canHold = useCanPerform('update:patients:phi');
  const canDischarge = useCanPerform('discharge:patients');
  const canAdmit = useCanPerform('admit:patients');
  const canManageSubscription = useCanPerform('manage:subscription');
  // Phase 14 DAT-01 / DSC-06: patient export and transfer record packet (owner, admin)
  const canExport = useCanPerform('export:patient');

  const [dialog, setDialog] = useState<'hold' | 'resume' | 'discharge' | 'admit' | 'readmit' | 'rocPrompt' | 'deleteSample' | 'export' | null>(null);
  const [exportKind, setExportKind] = useState<ExportKind>('full');
  const [busy, setBusy] = useState(false);
  const [letterBusy, setLetterBusy] = useState(false);
  const [holdHistory, setHoldHistory] = useState<PatientHoldHistoryEntry[] | null>(null);
  const [holdHistoryError, setHoldHistoryError] = useState<string | null>(null);
  const [paReviewNeeded, setPaReviewNeeded] = useState(false);
  const [ordersConfirmed, setOrdersConfirmed] = useState(false);
  // Phase 16E: reload the discharge / transfer summary banner after a discharge, hold or resume
  const [summaryRefresh, setSummaryRefresh] = useState(0);

  const patientId = patient?.id ?? '';
  const status = patient?.status;
  const holdDays = daysSince(patient?.holdStartDate);
  const holdIsLong = status === 'ON_HOLD' && holdDays !== null && holdDays > HOLD_PA_REVIEW_DAYS;

  // HLD-05: hold older than 14 days and an APPROVED PA → review banner
  useEffect(() => {
    setPaReviewNeeded(false);
    if (!holdIsLong || !patientId) return undefined;
    let cancelled = false;
    PatientLifecycleService.listPriorAuths(patientId)
      .then((pas) => {
        if (!cancelled) setPaReviewNeeded(pas.some(isApprovedPa));
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [holdIsLong, patientId]);

  useEffect(() => {
    setHoldHistory(null);
    setHoldHistoryError(null);
  }, [patientId, status]);

  const ageBanners = useMemo(
    () => (patient && status !== 'INACTIVE' ? getPatientAgeBanners(patient.dob) : []),
    [patient, status]
  );
  const socIncomplete = status === 'ACTIVE' && isSocChecklistIncomplete(dashboard);

  if (!patient) return null;

  const changed = (updated: PatientData, message: string) => {
    onPatientChanged(updated);
    notifyPatientChartUpdated(updated.id);
    setSummaryRefresh((n) => n + 1);
    showToast(message, { variant: 'success' });
  };

  const handleResume = async () => {
    const wasHospitalized = patient.holdReasonCode === 'HOSPITALIZED';
    setBusy(true);
    try {
      const updated = await resumePatient(patientId);
      changed(updated, 'Patient resumed.');
      if (wasHospitalized) {
        setOrdersConfirmed(false);
        setDialog('rocPrompt');
      } else {
        setDialog(null);
      }
    } catch (err) {
      // Phase 21: a resume is checked against a hard cap (402 PATIENT_CAP_REACHED on Starter)
      showToast(getApiErrorCode(err) === 'PATIENT_CAP_REACHED' ? describeApiError(err) : err instanceof Error ? err.message : 'Unable to resume the patient.', { variant: 'error' });
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  const handleReadmit = async (reason: string) => {
    setBusy(true);
    try {
      const updated = await readmitPatient(patientId, reason);
      changed(updated, 'Patient readmitted — pending admission.');
      setDialog(null);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Unable to readmit the patient.', { variant: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleAdmitted = async (result: AdmitPatientResponse) => {
    setDialog(null);
    if (result.warnings.length > 0) {
      showToast('Patient admitted with open onboarding items.', { variant: 'warning' });
    }
    try {
      const updated = await fetchPatientById(patientId);
      changed(updated, 'Patient admitted.');
    } catch {
      notifyPatientChartUpdated(patientId);
    }
  };

  const handleDeleteSample = async () => {
    setBusy(true);
    try {
      await SubscriptionService.deleteSamplePatient(patientId);
      setDialog(null);
      showToast('Sample patient deleted.', { variant: 'success' });
      onSampleDeleted?.();
    } catch (err) {
      showToast(describeApiError(err, 'Unable to delete the sample patient.'), { variant: 'error' });
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  /** 16E.1 (DSC-20): the written notice letter, filed in the documents library and opened for printing. */
  const downloadNoticeLetter = async () => {
    setLetterBusy(true);
    try {
      const link = await DischargeSummaryService.generateNoticeLetter(patientId);
      window.open(link.url, '_blank', 'noopener');
      showToast('Notice letter saved to the patient documents.', { variant: 'success' });
    } catch (err) {
      showToast(describeApiError(err, 'Unable to generate the notice letter.'), { variant: 'error' });
    } finally {
      setLetterBusy(false);
    }
  };

  const openExport = (kind: ExportKind) => {
    setExportKind(kind);
    setDialog('export');
  };

  const loadHoldHistory = async () => {
    setHoldHistoryError(null);
    try {
      setHoldHistory(await fetchPatientHoldHistory(patientId));
    } catch (err) {
      setHoldHistoryError(err instanceof Error ? err.message : 'Unable to load hold history.');
    }
  };

  const holdDetails = [
    holdReasonLabel(patient.holdReasonCode),
    patient.holdStartDate ? `Started ${formatUsDate(patient.holdStartDate)}` : null,
    patient.holdExpectedReturnDate ? `Expected return ${formatUsDate(patient.holdExpectedReturnDate)}` : null,
  ].filter(Boolean);

  return (
    <div className="mx-3 mt-3 space-y-2 md:mx-5" data-testid="patient-lifecycle-bar">
      {patient.isSample && (
        <Banner
          variant="neutral"
          title="Sample — not real PHI"
          data-testid="sample-patient-banner"
          action={
            canManageSubscription ? (
              <button
                type="button"
                onClick={() => setDialog('deleteSample')}
                disabled={busy}
                className={`${actionButtonClass} border-slate-400 bg-white text-slate-800 hover:bg-slate-50`}
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" /> Delete sample patient
              </button>
            ) : undefined
          }
        >
          This practice patient has made-up data. It never counts as an active patient and is left out of claims, EVV and audit exports.
        </Banner>
      )}

      {status === 'PENDING_ADMISSION' && (
        <Banner
          variant="info"
          title="Pending admission"
          action={
            canAdmit ? (
              <button
                type="button"
                onClick={() => setDialog('admit')}
                className={`${actionButtonClass} border-sky-400 bg-white text-sky-900 hover:bg-sky-100`}
              >
                <UserCheck className="h-4 w-4" aria-hidden="true" /> Admit
              </button>
            ) : undefined
          }
        >
          Referred but not yet admitted. Complete onboarding, then admit to start care.
        </Banner>
      )}

      {status === 'ON_HOLD' && (
        <Banner
          variant="warning"
          title="Patient is on hold"
          action={
            canHold ? (
              <button
                type="button"
                onClick={() => setDialog('resume')}
                disabled={busy}
                className={`${actionButtonClass} border-amber-400 bg-white text-amber-900 hover:bg-amber-100`}
              >
                <PlayCircle className="h-4 w-4" aria-hidden="true" /> Resume
              </button>
            ) : undefined
          }
        >
          <p>Visits can't be started until this patient is resumed.</p>
          <p className="mt-1 text-xs">{holdDetails.join(' • ')}</p>
          {patient.holdNote && <p className="mt-1 text-xs italic">{patient.holdNote}</p>}
          <button type="button" onClick={loadHoldHistory} className="mt-2 inline-flex items-center gap-1 text-xs font-medium underline">
            <History className="h-3.5 w-3.5" aria-hidden="true" /> {holdHistory ? 'Refresh hold history' : 'View hold history'}
          </button>
          {holdHistory && (
            <ul className="mt-2 space-y-1 border-t border-amber-200 pt-2 text-xs" aria-label="Hold history">
              {holdHistory.length === 0 ? (
                <li>No hold history found.</li>
              ) : (
                holdHistory.map((entry, index) => (
                  <li key={entry.holdId || `${entry.setAt || entry.startDate || 'entry'}-${index}`} className="flex flex-wrap gap-x-2">
                    <span className="font-medium">{holdReasonLabel(entry.reasonCode)}</span>
                    <span>
                      {formatUsDate(entry.startDate)} – {entry.endDate ? formatUsDate(entry.endDate) : 'ongoing'}
                    </span>
                    {entry.setBy && <span>placed by {entry.setBy}</span>}
                    {entry.resumedBy && <span>resumed by {entry.resumedBy}</span>}
                  </li>
                ))
              )}
            </ul>
          )}
          {holdHistoryError && <p className="mt-2 text-xs text-red-700">{holdHistoryError}</p>}
        </Banner>
      )}

      {status === 'ON_HOLD' && paReviewNeeded && (
        <Banner
          variant="warning"
          title={`Review the prior authorization — hold is over ${HOLD_PA_REVIEW_DAYS} days`}
          action={
            <button
              type="button"
              onClick={() => onNavigateTab('Prior Authorizations')}
              className={`${actionButtonClass} border-amber-400 bg-white text-amber-900 hover:bg-amber-100`}
            >
              Open prior authorizations
            </button>
          }
        >
          The patient has an approved PA while on hold. Check whether it needs an update with the payer.
        </Banner>
      )}

      {status === 'INACTIVE' && (
        <Banner
          variant="neutral"
          title={`Discharged ${formatUsDate(patient.dischargeDate)} — ${dischargeReasonLabel(patient.dischargeReason)}`}
          action={
            canDischarge || (canExport && !patient.isSample) ? (
              <div className="flex flex-wrap gap-2">
                {canExport && !patient.isSample && (
                  <button
                    type="button"
                    onClick={() => openExport(patient.dischargeReason === 'TRANSFERRED' ? 'transfer_packet' : 'full')}
                    className={`${actionButtonClass} border-slate-400 bg-white text-slate-800 hover:bg-slate-50`}
                  >
                    <FileArchive className="h-4 w-4" aria-hidden="true" /> {patient.dischargeReason === 'TRANSFERRED' ? 'Record packet' : 'Export record'}
                  </button>
                )}
                {canDischarge && patient.dischargeNotice && (
                  <button
                    type="button"
                    disabled={letterBusy}
                    onClick={downloadNoticeLetter}
                    className={`${actionButtonClass} border-slate-400 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-50`}
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" /> {letterBusy ? 'Preparing…' : 'Notice letter'}
                  </button>
                )}
                {canDischarge && (
                  <button
                    type="button"
                    onClick={() => setDialog('readmit')}
                    className={`${actionButtonClass} border-slate-400 bg-white text-slate-800 hover:bg-slate-50`}
                  >
                    <RotateCcw className="h-4 w-4" aria-hidden="true" /> Readmit
                  </button>
                )}
              </div>
            ) : undefined
          }
        >
          {patient.deceasedDate && <p>Date of death: {formatUsDate(patient.deceasedDate)}</p>}
          <p>This chart is read-only.</p>
        </Banner>
      )}

      <DischargeSummaryBanner patientId={patientId} status={status} refreshKey={summaryRefresh} />

      {socIncomplete && (
        <Banner
          variant="warning"
          title="Start-of-care checklist incomplete"
          action={
            <button
              type="button"
              onClick={() => onNavigateTab('Overview')}
              className={`${actionButtonClass} border-amber-400 bg-white text-amber-900 hover:bg-amber-100`}
            >
              Open checklist
            </button>
          }
        >
          This patient was admitted with open onboarding items.
        </Banner>
      )}

      {ageBanners.map((banner) => (
        <Banner key={banner.kind} variant="info" title={banner.message} data-testid={`age-banner-${banner.age}`}>
          {banner.age === 18
            ? 'Consent moves to the patient at 18. Review guardians and consents.'
            : 'Pediatric private duty nursing ends at 21. Plan the transition.'}
        </Banner>
      ))}

      {(status === 'ACTIVE' || status === 'ON_HOLD' || status === 'PENDING_ADMISSION') && (canHold || canDischarge || (canExport && !patient.isSample)) && (
        <div className="flex flex-wrap justify-end gap-2">
          {canExport && !patient.isSample && (
            <button
              type="button"
              onClick={() => openExport('full')}
              disabled={busy}
              className={`${actionButtonClass} border-slate-300 bg-white text-slate-800 hover:bg-slate-50`}
            >
              <FileArchive className="h-4 w-4" aria-hidden="true" /> Export record
            </button>
          )}
          {status === 'ACTIVE' && canHold && (
            <button
              type="button"
              onClick={() => setDialog('hold')}
              disabled={busy}
              className={`${actionButtonClass} border-amber-300 bg-amber-50 text-amber-900 hover:bg-amber-100`}
            >
              <PauseCircle className="h-4 w-4" aria-hidden="true" /> Put on hold
            </button>
          )}
          {canDischarge && status !== 'PENDING_ADMISSION' && (
            <button
              type="button"
              onClick={() => setDialog('discharge')}
              disabled={busy}
              className={`${actionButtonClass} border-slate-300 bg-white text-slate-800 hover:bg-slate-50`}
            >
              <LogOut className="h-4 w-4" aria-hidden="true" /> Discharge
            </button>
          )}
        </div>
      )}

      <HoldDialog
        open={dialog === 'hold'}
        patientId={patientId}
        onCancel={() => setDialog(null)}
        onHeld={(updated) => {
          setDialog(null);
          changed(updated, 'Patient placed on hold.');
        }}
      />

      <ConfirmDialog
        open={dialog === 'resume'}
        title="Resume patient"
        message="Resume this patient so visits can start again?"
        confirmLabel="Resume"
        busy={busy}
        onConfirm={handleResume}
        onCancel={() => setDialog(null)}
      />

      <DischargeDialog
        open={dialog === 'discharge'}
        patientId={patientId}
        dob={patient.dob}
        onCancel={() => setDialog(null)}
        onDischarged={(updated) => {
          changed(updated, 'Patient discharged.');
          // DSC-06: a transfer goes straight to the record packet for the receiving agency
          if (updated.dischargeReason === 'TRANSFERRED' && canExport && !updated.isSample) {
            setExportKind('transfer_packet');
            setDialog('export');
          } else {
            setDialog(null);
          }
        }}
      />

      <ConfirmDialog
        open={dialog === 'deleteSample'}
        title="Delete sample patient"
        message="Delete the sample patient and all of its practice records? This cannot be undone."
        confirmLabel="Delete sample patient"
        destructive
        busy={busy}
        onConfirm={handleDeleteSample}
        onCancel={() => setDialog(null)}
      />

      <ExportDialog
        open={dialog === 'export'}
        scope="patient"
        patientId={patientId}
        initialKind={exportKind}
        onClose={() => setDialog(null)}
      />

      <AdmitDialog
        open={dialog === 'admit'}
        patientId={patientId}
        onCancel={() => setDialog(null)}
        onAdmitted={handleAdmitted}
      />

      <ReasonDialog
        open={dialog === 'readmit'}
        title="Readmit patient"
        description="The patient returns to Pending admission and must be admitted again. The reason is saved to the audit history."
        label="Reason for readmission"
        confirmLabel="Readmit"
        busy={busy}
        onConfirm={handleReadmit}
        onCancel={() => setDialog(null)}
      />

      <Dialog open={dialog === 'rocPrompt'} onClose={() => setDialog(null)} labelledBy="roc-prompt-title">
        <div className="text-left">
          <h3 id="roc-prompt-title" className="text-lg font-semibold text-slate-800 pr-6">
            Resumed after hospitalization
          </h3>
          <ol className="mt-3 space-y-3 text-sm text-slate-700 list-decimal pl-5">
            <li>
              <p>Create ROC assessment?</p>
              <button
                type="button"
                onClick={() => {
                  setDialog(null);
                  onNavigateTab('Assessments');
                }}
                className="mt-1 rounded border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-800 hover:bg-slate-50"
              >
                Go to Assessments
              </button>
            </li>
            <li>
              <label className="flex items-start gap-2">
                <input
                  type="checkbox"
                  checked={ordersConfirmed}
                  onChange={(e) => setOrdersConfirmed(e.target.checked)}
                  className="mt-0.5"
                />
                <span>Confirm physician orders are current</span>
              </label>
            </li>
          </ol>
          <div className="mt-5 flex justify-end">
            <button
              type="button"
              onClick={() => setDialog(null)}
              disabled={!ordersConfirmed}
              className="px-4 py-2 rounded-md text-sm bg-slate-800 hover:bg-slate-700 text-white disabled:opacity-50"
            >
              Done
            </button>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default PatientLifecycleBar;
